import { useCallback } from 'react';
import { useNotification } from '@/common/components/notification/NotificationContext';
import { isFileSystemAccessSupported } from '@/common/utils/fileSystemAccess';

interface UseDirectoryPickerOptions {
  validateNonEmpty?: boolean;
  mode?: 'read' | 'readwrite';
}

interface UseDirectoryPickerReturn {
  selectDirectory: () => Promise<FileSystemDirectoryHandle | null>;
}

export const useDirectoryPicker = (
  options: UseDirectoryPickerOptions = {}
): UseDirectoryPickerReturn => {
  const { validateNonEmpty = true, mode = 'readwrite' } = options;
  const { showError } = useNotification();

  const selectDirectory =
    useCallback(async (): Promise<FileSystemDirectoryHandle | null> => {
      // Check browser compatibility first
      if (!isFileSystemAccessSupported()) {
        showError(
          "Your browser doesn't support the File System Access API. Please use Chrome, Edge, or another Chromium-based browser."
        );
        return null;
      }

      try {
        // Let user select the project directory
        const dirHandle = await window.showDirectoryPicker({
          mode,
        });

        // Validate directory if requested
        if (validateNonEmpty) {
          try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const entries: any = (dirHandle as any).entries();
            const firstEntry = await entries.next();
            if (firstEntry.done) {
              showError(
                'Selected directory appears to be empty. Please select the correct project folder.'
              );
              return null;
            }
          } catch (error) {
            console.warn('Could not validate directory contents:', error);
            // Continue anyway if validation fails
          }
        }

        return dirHandle;
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          // User cancelled the picker
          return null;
        }

        // Better error handling with specific error types
        console.error('Error selecting directory:', error);

        if (error instanceof Error) {
          switch (error.name) {
            case 'NotAllowedError':
              showError(
                'Permission denied. Please ensure you have access to the selected directory.'
              );
              break;
            case 'SecurityError':
              showError(
                'Security restriction. Some directories cannot be accessed for security reasons.'
              );
              break;
            case 'NotSupportedError':
              showError(
                'This operation is not supported in your current browser context.'
              );
              break;
            default:
              showError(
                `Failed to access the selected directory: ${error.message}. Please try again.`
              );
          }
        } else {
          showError(
            'Failed to access the selected directory. Please try again.'
          );
        }

        return null;
      }
    }, [mode, validateNonEmpty, showError]);

  return { selectDirectory };
};
